// Engine Orders (Management › Planning & Delivery): the season's boats past 70% assembly whose engines
// aren't installed yet (the Overview's Engine Install isn't Yes), most complete first, with two ticks each:
//   • Called sales — ticking it puts the boat's Engine Install on the Overview to Hold; unticking puts back
//     what was there (features/engineOrders.ts, apps/worker/src/routes/engineOrders.ts);
//   • Engines delivered to shipyard.
// Each tick says who ticked it and when. What the Overview already says ticks them too: Engine Install on
// Hold (set there by hand) ticks Called sales, and Yes ticks both; such a tick says it's from the Overview
// and is changed there, not here (effectiveEngineOrder in packages/shared domain/engineOrder.ts). The
// headline counts the boats still to call, the ones waiting for their engines and the ones whose engines
// are at the yard; each figure filters the list. People who may only view the Overview see the ticks but
// can't change them. A boat drops off once its engine is in.
import { useEffect, useMemo, useState } from 'react';
import { assemblyPctNum, effectiveEngineOrder, emptyEngineOrder, needsEngineOrder, parseDeliveryDate, ENGINE_ORDER_MIN_PCT, type EffectiveEngineOrder, type OverviewVessel } from '@technohull/shared';
import { useAuth } from '../auth/AuthContext.js';
import { useOverview } from '../features/hooks.js';
import { useEngineOrders, useSetEngineOrder, type EngineOrder } from '../features/engineOrders.js';
import { useNameResolver } from '../features/directory.js';
import { EmptyState, ErrorState, Fact, FactEnd, FactSentence, InlineError, LoadingState, PageHeader, Pill, SearchInput, SyncLabel } from '../components/ui.js';
import { IconCheck, IconPause } from '../components/icons.js';
import { useDateFormatter } from '../lib/format.js';
import { useIsPhone } from '../lib/media.js';
import { useTabState } from '../lib/tabState.js';
import { fold, hasText } from '../lib/search.js';

type Stage = 'call' | 'waiting' | 'delivered';
/** where a boat's engines are: Sales still to call, called and waiting, or at the shipyard. */
const stageOf = (o: EngineOrder): Stage => (o.delivered ? 'delivered' : o.calledSales ? 'waiting' : 'call');
const plural = (n: number, one: string, many = `${one}s`) => (n === 1 ? one : many);

export function EngineOrders() {
  const { data, isLoading, isFetching, error, refetch } = useOverview();
  const orders = useEngineOrders();
  const set = useSetEngineOrder(data?.season);
  const { can } = useAuth();
  const canWrite = can('overview:write');
  const nameOf = useNameResolver();
  const df = useDateFormatter();
  const isPhone = useIsPhone();
  const [q, setQ] = useState('');
  const [show, setShow] = useTabState<Stage | 'all'>('engines.show', 'all');

  // the app's ticks, and what the Overview's Engine Install already says (Hold: Sales called; Yes: both)
  const orderOf = (v: OverviewVessel): EffectiveEngineOrder =>
    effectiveEngineOrder(orders.data?.orders[v.sap.trim()] ?? emptyEngineOrder(v.sap.trim()), v.engineInstall);
  // the boats past 70% without their engines, most complete first
  const boats = useMemo(
    () => (data?.vessels ?? []).filter(needsEngineOrder).sort((a, b) => assemblyPctNum(b) - assemblyPctNum(a) || a.name.localeCompare(b.name)),
    [data],
  );
  const counts = useMemo(() => {
    const c: Record<Stage, number> = { call: 0, waiting: 0, delivered: 0 };
    for (const v of boats) c[stageOf(orderOf(v))]++;
    return c;
  }, [boats, orders.data]); // eslint-disable-line react-hooks/exhaustive-deps
  const rows = useMemo(() => {
    const query = fold(q.trim());
    return boats.filter((v) => (show === 'all' || stageOf(orderOf(v)) === show) && hasText(query, `${v.sap} ${v.name} ${v.model}`));
  }, [boats, show, q, orders.data]); // eslint-disable-line react-hooks/exhaustive-deps

  const busy = (sap: string, field: 'calledSales' | 'delivered') => set.isPending && set.variables?.sap === sap && set.variables[field] !== undefined;
  // a tick shows the moment it's clicked: the saved ticks take over once the list has it (straight away,
  // before the server answers), so a controlled box never springs back in between
  const [flipped, setFlipped] = useState<Record<string, boolean>>({});
  useEffect(() => { setFlipped({}); }, [orders.data]);
  const tick = (v: OverviewVessel, field: 'calledSales' | 'delivered', on: boolean) => {
    setFlipped((f) => ({ ...f, [`${v.sap.trim()}:${field}`]: on }));
    set.mutate({ sap: v.sap.trim(), [field]: on });
  };
  const stamp = (by: string, at: string) => [by ? nameOf(by) : '', at ? df.dateTime(at, { year: 'auto' }) : ''].filter(Boolean).join(' · ');

  const fact = (stage: Stage, text: string, what: string, tone: 'warn' | 'info' | 'ok') => (
    <Fact
      tone={counts[stage] ? tone : undefined}
      icon={<span className="al-fact-tick" aria-hidden="true"><IconCheck size={12} /></span>}
      pressed={show === stage}
      onClick={() => setShow(show === stage ? 'all' : stage)}
      title={show === stage ? 'Show every boat again' : `Show only the boats ${what}`}
    >{text}</Fact>
  );

  const engineCell = (v: OverviewVessel) => {
    const e = v.engineInstall.trim();
    if (/^hold$/i.test(e)) return <Pill kind="warn"><IconPause size={10} /> Hold</Pill>;
    return e ? <Pill>{e}</Pill> : <span className="muted">–</span>;
  };
  const tickBox = (v: OverviewVessel, o: EffectiveEngineOrder, field: 'calledSales' | 'delivered', label: string) => {
    // ticked by the Overview alone: it shows so, and changes there
    const fromOv = field === 'calledSales' ? o.calledSalesFromOverview : o.deliveredFromOverview;
    const on = fromOv || (flipped[`${v.sap.trim()}:${field}`] ?? (field === 'calledSales' ? o.calledSales : o.delivered));
    const by = fromOv
      ? `From the Overview: ${v.engineInstall.trim()}`
      : field === 'calledSales' ? stamp(o.calledSalesBy, o.calledSalesAt) : stamp(o.deliveredBy, o.deliveredAt);
    return (
      <label
        className={`eo-tick${on ? ' on' : ''}${fromOv ? ' is-ov' : ''}`}
        title={fromOv ? `The Overview's Engine Install says ${v.engineInstall.trim()}: change it there` : undefined}
      >
        <input
          type="checkbox"
          checked={on}
          disabled={!canWrite || fromOv || busy(v.sap.trim(), field)}
          onChange={(e) => tick(v, field, e.currentTarget.checked)}
          aria-label={`${label}, ${v.name}`}
        />
        <span className="eo-tick-t">
          <span className="eo-tick-l">{label}</span>
          {on && by && <span className="eo-tick-by">{by}</span>}
        </span>
      </label>
    );
  };

  return (
    <>
      <PageHeader title="Engine Orders">
        <SearchInput value={q} onChange={setQ} placeholder="Search SAP / vessel / model" width={220} />
        <button className="btn btn-sm" disabled={isFetching || orders.isFetching} onClick={() => { void refetch(); void orders.refetch(); }}>
          <SyncLabel busy={isFetching || orders.isFetching} />
        </button>
      </PageHeader>

      {isLoading && <LoadingState label="Loading the Overview…" />}
      {error && <ErrorState error={error} onRetry={() => void refetch()} />}
      {orders.error && <InlineError error={orders.error} />}
      {set.isError && <InlineError error={set.error} onDismiss={() => set.reset()} />}

      {data && (
        <>
          <FactSentence className="al-facts">
            {boats.length} {plural(boats.length, 'boat is', 'boats are')} past {ENGINE_ORDER_MIN_PCT}% without {plural(boats.length, 'its', 'their')} engines
            installed<FactEnd>:</FactEnd>{' '}
            {fact('call', `${counts.call} still to call Sales`, 'still to call Sales about', 'warn')}<FactEnd>,</FactEnd>{' '}
            {fact('waiting', `${counts.waiting} waiting for engines`, 'waiting for their engines', 'info')}{' '}
            and {fact('delivered', `${counts.delivered} with engines at the shipyard`, 'whose engines are at the shipyard', 'ok')}<FactEnd>.</FactEnd>
          </FactSentence>
          <FactSentence sub className="al-facts-sub">
            Season {data.season} Overview. Ticking Called sales puts the boat’s Engine Install on Hold there; unticking it puts back what was there.
            {' '}A boat already on Hold there shows Called sales ticked, from the Overview.
            {' '}{isPhone ? 'Tap' : 'Click'} a figure to show only those boats.
          </FactSentence>

          <div className="page-panel al-panel">
            <div className="tbl-wrap">
              <table className="tbl tbl-cards eo-tbl">
                <thead>
                  <tr>
                    <th>SAP</th><th>Vessel</th><th>Model</th><th>Assembly %</th><th>Engine install</th><th>Delivery</th>
                    <th>Called sales</th><th>Engines delivered to shipyard</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((v) => {
                    const o = orderOf(v);
                    const pct = Math.round(assemblyPctNum(v));
                    const del = parseDeliveryDate(v) ? df.date(v.deliveryDate) : v.deliveryDate || '–';
                    return (
                      <tr key={v.rowId} className={`eo-row eo-${stageOf(o)}`}>
                        <td className="tc-end eo-sap">{v.sap}</td>
                        <td className="tc-title">{v.name}</td>
                        <td className="tc-hide">{v.model}</td>
                        <td className="tc-hide"><span className="eo-pct"><span className="eo-bar" style={{ width: `${Math.min(100, pct)}%` }} /></span>{pct}%</td>
                        <td className="tc-lbl" data-label="Engine install">{engineCell(v)}</td>
                        <td className="tc-hide">{del}</td>
                        <td className="tc-meta eo-meta">{[v.model, `${pct}%`, `DEL ${del}`].filter(Boolean).join(' · ')}</td>
                        <td className="tc-full eo-tick-td">{tickBox(v, o, 'calledSales', 'Called sales')}</td>
                        <td className="tc-full eo-tick-td">{tickBox(v, o, 'delivered', 'Engines delivered to shipyard')}</td>
                      </tr>
                    );
                  })}
                  {rows.length === 0 && (
                    <tr>
                      <td colSpan={8}>
                        <EmptyState
                          title={boats.length ? 'No boats match' : 'No engines to order'}
                          hint={boats.length ? 'Try another figure above, or clear the search.' : `Every boat past ${ENGINE_ORDER_MIN_PCT}% this season has its engine installed.`}
                        />
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </>
  );
}
